import { ref } from 'vue'
import axios from 'axios'
import { useModals } from '@/shared/composables/useModals.js'
import type { ComingSoonKey } from './comingSoon'

export const COMING_SOON_MODAL_NAME = 'sidebar-coming-soon'
const COLLAPSED_STORAGE_KEY = 'gm-sidebar-collapsed'
const LABS_STORAGE_KEY = 'gm-sidebar-labs-open'

// One copy for the whole app. Only the browser writes these (clicks and
// localStorage after mount), so the SSR server never holds a member's state.
const collapsed = ref(false)
const comingSoonKey = ref<ComingSoonKey | null>(null)
const isMoreSheetOpen = ref(false)
// The groups the member opened with the chevron, by item key. It applies
// away from the group's own pages; on them the group is always open. It is
// not saved: a page load shows only the group of the current page open.
const openGroups = ref<Record<string, boolean>>({})
// The Labs group is closed until the member opens it with its chevron.
// The choice is saved in this browser, like the fold.
const isLabsOpen = ref(false)

export const useSidebarState = () => {
  const { openModal } = useModals()

  // A click on a Soon item opens the window and counts one click of demand.
  // A failed count is not worth an error: the window still opens.
  const openComingSoon = (key: ComingSoonKey) => {
    comingSoonKey.value = key
    isMoreSheetOpen.value = false
    openModal(COMING_SOON_MODAL_NAME)
    axios.post(`/feature-interactions/coming_soon_${key}/seen`).catch(() => {
      // Counting is best effort. The window already shows.
    })
  }

  const loadCollapsed = () => {
    try {
      collapsed.value =
        window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
      isLabsOpen.value = window.localStorage.getItem(LABS_STORAGE_KEY) === '1'
    } catch {
      collapsed.value = false
      isLabsOpen.value = false
    }
  }

  const toggleCollapsed = () => {
    collapsed.value = !collapsed.value
    try {
      window.localStorage.setItem(
        COLLAPSED_STORAGE_KEY,
        collapsed.value ? '1' : '0',
      )
    } catch {
      // Private mode or blocked storage: the toggle works for this visit only.
    }
  }

  const toggleLabs = () => {
    isLabsOpen.value = !isLabsOpen.value
    try {
      window.localStorage.setItem(
        LABS_STORAGE_KEY,
        isLabsOpen.value ? '1' : '0',
      )
    } catch {
      // Private mode or blocked storage: the toggle works for this visit only.
    }
  }

  const toggleGroup = (key: string, isOpen: boolean) => {
    openGroups.value = { ...openGroups.value, [key]: !isOpen }
  }

  return {
    collapsed,
    openGroups,
    isLabsOpen,
    comingSoonKey,
    isMoreSheetOpen,
    openComingSoon,
    loadCollapsed,
    toggleCollapsed,
    toggleLabs,
    toggleGroup,
  }
}
